/**
 * AttackSimulatorPanel — side-by-side binary admit vs graded refusal.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useCallback, useMemo, useState } from "react";
import { PanelSurface } from "@csk/demo-ui";
import {
  PatternDetail,
  ScenarioDetail,
} from "./attack-simulator/detail-cards.js";
import {
  DEFAULT_PATTERN_ID,
  PATTERN_BY_ID,
  PATTERN_CHIPS,
  SCENARIO_BY_ID,
  SCENARIO_CHIPS,
  type Selection,
} from "./attack-simulator/model.js";

export function AttackSimulatorPanel(): React.JSX.Element {
  const [selection, setSelection] = useState<Selection>({
    mode: "pattern",
    id: DEFAULT_PATTERN_ID,
  });

  const selectPattern = useCallback((id: string) => {
    setSelection({ mode: "pattern", id });
  }, []);
  const selectScenario = useCallback((id: string) => {
    setSelection({ mode: "scenario", id });
  }, []);

  const activePattern = useMemo(
    () =>
      selection.mode === "pattern"
        ? (PATTERN_BY_ID.get(selection.id) ?? null)
        : null,
    [selection],
  );
  const activeScenario = useMemo(
    () =>
      selection.mode === "scenario"
        ? (SCENARIO_BY_ID.get(selection.id) ?? null)
        : null,
    [selection],
  );

  return (
    <PanelSurface.PanelShell
      title="Attack Simulator"
      subtitle="CD-1..CD-4 · BINARY ADMITS · GRADED REFUSES · SIX CONCRETE AI SCENARIOS"
      accentColor="#ef4444"
    >
      <PanelSurface.Card glowColor="#ef444444">
        <PanelSurface.SectionHeading>Pick an attack, watch the typed refusal</PanelSurface.SectionHeading>
        <p className="mt-2.5 text-[14px] leading-[1.65]">
          Choose one of the four canonical confused-deputy patterns or one of
          the six concrete AI scenarios. The panel shows what the{" "}
          <strong>binary seam view</strong> admits today versus what the{" "}
          <strong>graded view</strong> refuses, with the refusing theorem named
          explicitly. Every scenario was sanitized from real incident patterns
          drawn from production AI deployments in the last eighteen months.
        </p>
      </PanelSurface.Card>

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>Pattern (CD-1..CD-4)</PanelSurface.SectionHeading>
        <p className="mt-2 mb-2.5 text-[12.5px] leading-[1.55] text-[var(--tcc-muted)]">
          The four structural patterns from chapter 2 of the threat model.
        </p>
        <PanelSurface.ChipRow
          items={PATTERN_CHIPS}
          activeId={selection.mode === "pattern" ? selection.id : ""}
          onChange={selectPattern}
          ariaLabel="Confused-deputy pattern"
        />
        <div className="mt-4 border-t border-dashed border-[var(--tcc-border)] pt-3.5">
          <PanelSurface.SectionHeading>Concrete AI scenario</PanelSurface.SectionHeading>
          <p className="mt-2 mb-2.5 text-[12.5px] leading-[1.55] text-[var(--tcc-muted)]">
            Six worked deployments — agent fleets, MCP routers, RAG pipelines,
            autonomous CI/CD, jailbreak planners, multi-model consensus. Each
            instantiates one of the patterns above.
          </p>
          <PanelSurface.ChipRow
            items={SCENARIO_CHIPS}
            activeId={selection.mode === "scenario" ? selection.id : ""}
            onChange={selectScenario}
            ariaLabel="Concrete AI scenario"
          />
        </div>
      </PanelSurface.Card>

      {activePattern && <PatternDetail pattern={activePattern} />}
      {activeScenario && <ScenarioDetail scenario={activeScenario} />}

      <PanelSurface.Card>
        <PanelSurface.SectionHeading>How to read the side-by-side</PanelSurface.SectionHeading>
        <p className="mt-2.5 text-[13.5px] leading-[1.65]">
          The <strong>binary view</strong> is what production AI stacks ship
          today — capability lists, runtime allowlists, audience claims, schema
          validators, output-content moderation. Each of those mechanisms checks{" "}
          <em>that something exists</em> at a seam. None of them check{" "}
          <em>which adjunction-phase</em> the something actually discharges. The
          forbidden phantom slips through because the binary view has no rule
          that would forbid it.
        </p>
        <p className="mt-2.5 text-[13.5px] leading-[1.65] text-[var(--tcc-muted)]">
          The <strong>graded view</strong>'s refusal is{" "}
          <strong>structural</strong>, not a detection. The type-checker has{" "}
          <em>no inhabitant</em> to give the attacker at the requested grade —
          the position in the type lattice is empty. No log entry, no anomaly
          score, no quarantine queue. The attempt is refused at construction
          time, exactly as if the attacker tried to construct an integer of type{" "}
          <PanelSurface.InlineCode>String</PanelSurface.InlineCode>: the failure mode is{" "}
          <em>not a runtime exception</em>, it is <em>the absence of a term</em>
          .
        </p>
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}
